import { useEffect, useRef, useState } from "react";

import PropTypes from "prop-types";
import { Html5Qrcode } from "html5-qrcode";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import DauTrang, { NhanDau } from "components/DauTrang";
import { MAU, SEMANTIC } from "assets/theme/avocadoBrand";
import { sotApi } from "api/sot";
import { giaoDichApi } from "api/giaoDich";
import useTour from "hooks/useTour";
import buocNhapXuatKho from "tours/nhapXuatKho";
import { dungScannerAnToan, tinhQrBox } from "lib/dungCameraQr";
import { nhanTrangThaiThanhToan } from "lib/nhanTrangThaiThanhToan";

const CAMERA_BOX_ID = "vung-quet-qr-kho";

/** Khối tóm tắt thu mua trên Sọt — farm + vài phiếu gần nhất, gọn cho mobile. */
function KhoiTomTatThuMua({ tomTat, loiTomTat }) {
  if (loiTomTat) {
    return (
      <MDBox mt={2}>
        <MDAlert color="warning" dismissible={false}>
          Không tải được thông tin thu mua — vẫn có thể cân xác nhận. {loiTomTat}
        </MDAlert>
      </MDBox>
    );
  }

  if (!tomTat) return null;

  const farms = tomTat.farms || [];
  const phieu = tomTat.phieu || [];
  const soPhieu = tomTat.soPhieu ?? 0;

  return (
    <MDBox
      mt={2}
      px={1.75}
      py={1.5}
      borderRadius="lg"
      sx={{
        backgroundColor: SEMANTIC.background,
        border: `1px solid ${SEMANTIC.border}`,
      }}
    >
      <MDTypography
        variant="caption"
        fontWeight="bold"
        display="block"
        sx={{ letterSpacing: "0.04em", textTransform: "uppercase", color: SEMANTIC.textSecondary }}
      >
        Thu mua trên Sọt này
      </MDTypography>

      {soPhieu === 0 ? (
        <MDTypography variant="button" color="text" display="block" mt={0.75}>
          Chưa có phiếu thu mua trên chu kỳ hiện tại.
        </MDTypography>
      ) : (
        <>
          <MDBox display="flex" flexWrap="wrap" gap={1} alignItems="baseline" mt={0.5} mb={1}>
            <MDTypography variant="button" fontWeight="medium">
              {soPhieu} phiếu
            </MDTypography>
            <MDTypography variant="caption" color="text">
              · tổng {tomTat.tongKg ?? 0} kg
              {farms.length > 0 ? ` · ${farms.length} người bán` : ""}
            </MDTypography>
          </MDBox>

          {phieu.length > 0 &&
            phieu.map((p) => {
              const tt = nhanTrangThaiThanhToan(p.trangThaiThanhToan);
              return (
                <MDBox
                  key={p.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  py={0.55}
                  sx={{ borderTop: `1px dashed ${SEMANTIC.border}` }}
                >
                  <MDBox minWidth={0} display="flex" alignItems="center" gap={0.75} flex={1}>
                    <Chip size="small" label={tt.label} color={tt.color} sx={{ flexShrink: 0 }} />
                    <MDBox minWidth={0}>
                      <MDTypography
                        variant="button"
                        fontWeight="medium"
                        display="block"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.tenChuVuon || "—"}
                      </MDTypography>
                      {p.tenVuon ? (
                        <MDTypography
                          variant="caption"
                          color="text"
                          display="block"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Vườn: {p.tenVuon}
                        </MDTypography>
                      ) : null}
                    </MDBox>
                  </MDBox>
                  <MDTypography
                    variant="button"
                    fontWeight="medium"
                    sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                  >
                    {p.tongKg ?? 0} kg
                  </MDTypography>
                </MDBox>
              );
            })}
        </>
      )}
    </MDBox>
  );
}

KhoiTomTatThuMua.propTypes = {
  tomTat: PropTypes.shape({
    soPhieu: PropTypes.number,
    tongKg: PropTypes.number,
    farms: PropTypes.arrayOf(
      PropTypes.shape({
        tenChuVuon: PropTypes.string,
        tenVuon: PropTypes.string,
        soPhieu: PropTypes.number,
        tongKg: PropTypes.number,
      })
    ),
    phieu: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        tenChuVuon: PropTypes.string,
        tenVuon: PropTypes.string,
        tenNhanVien: PropTypes.string,
        tongKg: PropTypes.number,
        trangThaiThanhToan: PropTypes.string,
        thoiGian: PropTypes.string,
      })
    ),
  }),
  loiTomTat: PropTypes.string,
};

KhoiTomTatThuMua.defaultProps = {
  tomTat: null,
  loiTomTat: "",
};

function NhapXuatKho() {
  const [maQr, setMaQr] = useState("");
  const [loi, setLoi] = useState("");
  const [dangTai, setDangTai] = useState(false);
  const [dungCamera, setDungCamera] = useState(true);
  const [loiCamera, setLoiCamera] = useState("");
  const [dangKhoiDongCamera, setDangKhoiDongCamera] = useState(true);

  // Sọt vừa quét, đang chờ nhân viên cân lại rồi mới xác nhận — trong lúc
  // này camera tạm dừng để không quét chồng lên Sọt khác. loaiThaoTac tự
  // suy ra từ trạng thái Sọt: chưa có trong kho -> nhập, đã có trong kho
  // -> xuất (cân lại sau ủ), không bắt nhân viên tự chọn nhập hay xuất.
  const [sotCho, setSotCho] = useState(null);
  const [soCan, setSoCan] = useState("");
  const [dangXacNhan, setDangXacNhan] = useState(false);
  const [daXuLy, setDaXuLy] = useState([]);
  const [tomTatThuMua, setTomTatThuMua] = useState(null);
  const [loiTomTat, setLoiTomTat] = useState("");

  const qrRef = useRef(null);
  const dangXuLyRef = useRef(false);
  const quetGanNhatRef = useRef({ ma: null, luc: 0 });

  async function tra(qrCode) {
    const ma = qrCode.trim();
    const gio = Date.now();
    // Camera đọc ~10 khung hình/giây: bỏ qua các lần quét trùng mã trong lúc
    // QR vẫn còn nằm trong khung hình, tránh spam truy vấn Firestore.
    if (quetGanNhatRef.current.ma === ma && gio - quetGanNhatRef.current.luc < 3000) return;
    quetGanNhatRef.current = { ma, luc: gio };

    if (dangXuLyRef.current) return;
    dangXuLyRef.current = true;
    setLoi("");
    setLoiTomTat("");
    setTomTatThuMua(null);
    setDangTai(true);
    try {
      const sot = await sotApi.theoQr(ma);
      let loaiThaoTac;
      if (sot.trangThai === "da_nhap_kho") {
        loaiThaoTac = "xuat";
      } else if (sot.khoiLuongHienTai) {
        // Chặn sớm nếu còn phiếu chưa thanh toán — trước khi cân lại.
        await giaoDichApi.kiemTraThanhToanTruocNhapKho(sot.id, sot.chuKyHienTai ?? null);
        loaiThaoTac = "nhap";
      } else {
        throw new Error("Sọt này chưa có giao dịch thu mua nào, chưa thể nhập/xuất kho.");
      }

      // Tóm tắt farm/phiếu — lỗi tải không chặn cân (khác chặn TT khi nhập).
      let tomTat = null;
      let loiTom = "";
      try {
        tomTat = await giaoDichApi.tomTatThuMuaTheoSot(sot.id, sot.chuKyHienTai ?? null);
      } catch (errTom) {
        loiTom = errTom.message || "Không tải được thông tin thu mua.";
      }

      await dungScannerAnToan(qrRef.current);
      qrRef.current = null;
      setSotCho({ ...sot, loaiThaoTac });
      setTomTatThuMua(tomTat);
      setLoiTomTat(loiTom);
      // Gợi ý điểm khởi đầu bằng số cân lần trước — nhân viên chỉnh lại theo
      // số cân thực tế (xuất kho thường hao hụt so với lúc nhập, do ủ).
      setSoCan(
        loaiThaoTac === "xuat" && sot.khoiLuongCanLai != null ? String(sot.khoiLuongCanLai) : ""
      );
      setMaQr("");
    } catch (err) {
      setLoi(err.message || "Không tìm thấy Sọt với mã QR này.");
      setTomTatThuMua(null);
      setLoiTomTat("");
    } finally {
      dangXuLyRef.current = false;
      setDangTai(false);
    }
  }

  function xacNhanNhapTay(e) {
    e.preventDefault();
    tra(maQr);
  }

  function huySot() {
    setSotCho(null);
    setSoCan("");
    setLoi("");
    setTomTatThuMua(null);
    setLoiTomTat("");
  }

  // Hai nút chuyển chế độ chỉ có tác dụng khi ĐỔI sang chế độ còn lại — bấm
  // vào chế độ đang bật là không làm gì, nên `dungCamera` chỉ đổi giá trị đúng
  // lúc người dùng thật sự chuyển chế độ và vòng đời camera bên dưới không bị
  // chạy lại thừa.
  function chuyenSangQuetQr() {
    if (dungCamera) return;
    setLoi("");
    setDungCamera(true);
  }

  async function chuyenSangNhapTay() {
    if (!dungCamera) return;
    // Dừng camera trước khi ẩn UI — không unmount #CAMERA_BOX_ID (chỉ CSS),
    // tránh AbortError "play() was interrupted because the media was removed".
    await dungScannerAnToan(qrRef.current);
    qrRef.current = null;
    setDungCamera(false);
  }

  async function xacNhan(e) {
    e.preventDefault();
    setDangXacNhan(true);
    setLoi("");
    try {
      const soCanSo = Number(soCan);
      if (!Number.isFinite(soCanSo) || soCanSo <= 0) {
        throw new Error("Nhập khối lượng lớn hơn 0.");
      }
      if (sotCho.loaiThaoTac === "nhap") {
        const thuMua = Number(sotCho.khoiLuongHienTai);
        if (Number.isFinite(thuMua) && soCanSo > thuMua) {
          throw new Error(
            `Khối lượng nhập kho không được lớn hơn số thu mua trên Sọt (${thuMua} kg).`
          );
        }
        await sotApi.nhapKho(sotCho.id, { khoiLuongCanLai: soCanSo });
        setDaXuLy((ds) => [
          {
            loai: "nhap",
            maSot: sotCho.maSot,
            tuKg: sotCho.khoiLuongHienTai,
            denKg: soCanSo,
            luc: new Date().toISOString(),
          },
          ...ds,
        ]);
      } else {
        const tranTran = sotCho.khoiLuongCanLai != null ? Number(sotCho.khoiLuongCanLai) : null;
        if (tranTran != null && Number.isFinite(tranTran) && soCanSo > tranTran) {
          throw new Error(`Khối lượng xuất không được lớn hơn số đã nhập kho (${tranTran} kg).`);
        }
        const thuMua = Number(sotCho.khoiLuongHienTai);
        if (Number.isFinite(thuMua) && thuMua > 0 && soCanSo > thuMua) {
          throw new Error(`Khối lượng xuất không được lớn hơn số thu mua trên Sọt (${thuMua} kg).`);
        }
        await sotApi.xuatKho(sotCho.id, { khoiLuongXuat: soCanSo });
        setDaXuLy((ds) => [
          {
            loai: "xuat",
            maSot: sotCho.maSot,
            tuKg: sotCho.khoiLuongCanLai,
            denKg: soCanSo,
            luc: new Date().toISOString(),
          },
          ...ds,
        ]);
      }
      setSotCho(null);
      setSoCan("");
      setTomTatThuMua(null);
      setLoiTomTat("");
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangXacNhan(false);
    }
  }

  useEffect(() => {
    if (!dungCamera || sotCho) return undefined;

    let huy = false;
    const qr = new Html5Qrcode(CAMERA_BOX_ID);
    qrRef.current = qr;
    setLoiCamera("");
    setDangKhoiDongCamera(true);

    qr.start(
      { facingMode: "environment" },
      {
        fps: 10,
        // advanced/focusMode: nhiều điện thoại Android mặc định không auto-focus
        // liên tục khi mở camera qua getUserMedia, khiến mã QR in nhỏ/cầm gần
        // luôn bị mờ và không bao giờ đọc được dù camera "chạy" bình thường.
        // Trình duyệt không hỗ trợ constraint này sẽ tự bỏ qua, không lỗi.
        // (phải đặt trong videoConstraints — cameraIdOrConfig ở start() chỉ
        // cho phép đúng 1 key nên không nhét "advanced" vào đó được).
        videoConstraints: {
          facingMode: "environment",
          advanced: [{ focusMode: "continuous" }],
        },
        // Khung vuông cố định 250px có thể to hơn khung hình camera thật trên
        // màn hình hẹp/camera phân giải thấp — lúc đó thư viện không quét
        // được gì cả. Tính theo % kích thước khung hình thật để luôn vừa.
        qrbox: tinhQrBox,
        // Bộ giải mã JS thuần (zxing) đi kèm thư viện yếu hơn hẳn API quét mã
        // vạch gốc của trình duyệt (BarcodeDetector) — máy nào hỗ trợ (Chrome/
        // Edge Android phần lớn) sẽ tự chuyển sang dùng, máy không hỗ trợ vẫn
        // rơi về cách cũ, không lỗi gì thêm.
        experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      },
      (maQuetDuoc) => tra(maQuetDuoc),
      () => {} // lỗi decode mỗi frame không tìm thấy QR — bỏ qua, không phải lỗi thật
    )
      .then(() => {
        if (huy) {
          dungScannerAnToan(qr);
          return;
        }
        setDangKhoiDongCamera(false);
      })
      .catch((err) => {
        if (huy) return;
        setDangKhoiDongCamera(false);
        setDungCamera(false);
        const lyDo = err?.message ?? err;
        setLoiCamera(
          `Không mở được camera (${lyDo}). Hãy cấp quyền camera cho trình duyệt, hoặc nhập mã QR bằng tay bên dưới.`
        );
      });

    return () => {
      huy = true;
      // Luôn gọi dừng — không chỉ khi isScanning: start() có thể còn đang bay.
      dungScannerAnToan(qr);
      if (qrRef.current === qr) qrRef.current = null;
    };
  }, [dungCamera, sotCho]);

  const laXuat = sotCho?.loaiThaoTac === "xuat";
  const tranKgToiDa = sotCho
    ? laXuat
      ? sotCho.khoiLuongCanLai ?? sotCho.khoiLuongHienTai
      : sotCho.khoiLuongHienTai
    : null;

  useTour("kho-nhap-xuat-sot", sotCho ? [] : buocNhapXuatKho());

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <DauTrang
          nhan="Kho"
          tieuDe="Nhập / xuất kho Sọt"
          moTa="Hệ thống tự nhận biết Sọt chưa vào kho (nhập) hay đã trong kho (xuất, cân lại sau ủ)."
        />

        <Grid container justifyContent="center">
          <Grid item xs={12} sm={10} md={8} lg={6} xl={5}>
            {sotCho ? (
              <Card>
                <MDBox component="form" p={{ xs: 2.5, sm: 3 }} onSubmit={xacNhan}>
                  <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={0.5}>
                    <MDTypography variant="h4">Sọt {sotCho.maSot}</MDTypography>
                    <Chip
                      size="small"
                      label={laXuat ? "Xuất kho" : "Nhập kho"}
                      color={laXuat ? "warning" : "success"}
                    />
                  </MDBox>
                  <MDTypography variant="button" fontWeight="regular" color="text" display="block">
                    Cân lại Sọt rồi nhập số thực tế bên dưới.
                  </MDTypography>

                  <MDBox
                    mt={2}
                    px={1.75}
                    py={1.25}
                    borderRadius="lg"
                    sx={({ palette }) => ({
                      backgroundColor: palette.grey[100],
                      borderLeft: `3px solid ${palette.info.main}`,
                    })}
                  >
                    <MDTypography variant="caption" color="text" display="block" lineHeight={1.2}>
                      {laXuat ? "Đã ghi nhận lúc nhập kho" : "Đã ghi nhận lúc thu mua"}
                    </MDTypography>
                    <MDTypography variant="button" fontWeight="medium" display="block">
                      {(laXuat ? sotCho.khoiLuongCanLai : sotCho.khoiLuongHienTai) ?? "—"} kg
                    </MDTypography>
                    {sotCho.khoiLuongBi != null && (
                      <MDTypography variant="caption" color="text" display="block" mt={0.75}>
                        Khối lượng bì Sọt: <b>{sotCho.khoiLuongBi} kg</b> — cân xong nhớ trừ bì.
                      </MDTypography>
                    )}
                  </MDBox>

                  <KhoiTomTatThuMua tomTat={tomTatThuMua} loiTomTat={loiTomTat} />

                  <MDBox mt={2.5}>
                    <MDInput
                      type="number"
                      label={
                        laXuat ? "Khối lượng xuất (cân lại sau ủ, kg)" : "Khối lượng cân lại (kg)"
                      }
                      value={soCan}
                      onChange={(e) => setSoCan(e.target.value)}
                      fullWidth
                      autoFocus
                      required
                      inputProps={{
                        min: 0,
                        step: 0.1,
                        max: tranKgToiDa != null ? Number(tranKgToiDa) : undefined,
                      }}
                    />
                    {tranKgToiDa != null && (
                      <MDTypography variant="caption" color="text" display="block" mt={0.75}>
                        {laXuat
                          ? `Không được lớn hơn số đã nhập kho (${tranKgToiDa} kg).`
                          : `Không được lớn hơn số thu mua trên Sọt (${tranKgToiDa} kg).`}
                      </MDTypography>
                    )}
                  </MDBox>

                  {loi && (
                    <MDBox mt={2}>
                      <MDAlert color="error">{loi}</MDAlert>
                    </MDBox>
                  )}

                  <MDBox
                    display="flex"
                    gap={1}
                    mt={2.5}
                    pt={2}
                    sx={({ palette }) => ({ borderTop: `1px solid ${palette.grey[300]}` })}
                  >
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      onClick={huySot}
                      disabled={dangXacNhan}
                    >
                      Huỷ
                    </MDButton>
                    <MDButton
                      type="submit"
                      variant="gradient"
                      color="info"
                      fullWidth
                      disabled={dangXacNhan}
                    >
                      {dangXacNhan
                        ? "Đang lưu..."
                        : laXuat
                        ? "Xác nhận xuất kho"
                        : "Xác nhận nhập kho"}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            ) : (
              <Card>
                <MDBox p={{ xs: 2.5, sm: 3 }}>
                  <MDTypography variant="h5" mb={0.5}>
                    Quét QR Sọt
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    display="block"
                    mb={2.5}
                  >
                    Quét mã QR dán trên Sọt, hoặc nhập mã bằng tay.
                  </MDTypography>

                  <MDBox
                    id="tour-kho-che-do-quet"
                    display="flex"
                    gap={1}
                    p={0.5}
                    mb={2.5}
                    borderRadius="lg"
                    bgColor="grey-200"
                  >
                    <MDButton
                      fullWidth
                      variant={dungCamera ? "gradient" : "text"}
                      color={dungCamera ? "info" : "dark"}
                      onClick={chuyenSangQuetQr}
                    >
                      <Icon sx={{ mr: 0.75 }}>qr_code_scanner</Icon>
                      Quét QR
                    </MDButton>
                    <MDButton
                      fullWidth
                      variant={dungCamera ? "text" : "gradient"}
                      color={dungCamera ? "dark" : "info"}
                      onClick={chuyenSangNhapTay}
                    >
                      <Icon sx={{ mr: 0.75 }}>keyboard</Icon>
                      Nhập tay
                    </MDButton>
                  </MDBox>

                  {/* Giữ #CAMERA_BOX_ID luôn trong DOM — chỉ ẩn bằng CSS khi nhập tay,
                      tránh AbortError khi <video> bị gỡ giữa lúc play()/start(). */}
                  <MDBox sx={{ display: dungCamera ? "block" : "none" }}>
                    {/* Không ép aspect-ratio/max-height cho khung này: html5-qrcode tự
                        chèn <video> theo tỉ lệ camera thật, ép khung sẽ làm hình bị cắt
                        lệch với vùng quét mà thư viện vẽ lên. */}
                    <MDBox
                      id={CAMERA_BOX_ID}
                      sx={{
                        width: "100%",
                        minHeight: "250px",
                        borderRadius: "0.75rem",
                        overflow: "hidden",
                        backgroundColor: MAU.ink,
                      }}
                    />
                    <MDBox display="flex" alignItems="center" gap={0.75} mt={1}>
                      <Icon sx={{ color: MAU.pit, fontSize: "1rem" }}>center_focus_strong</Icon>
                      <MDTypography variant="caption" color="text">
                        {dangKhoiDongCamera
                          ? "Đang mở camera..."
                          : "Đưa mã QR trên Sọt vào giữa khung hình."}
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  {!dungCamera && (
                    <MDBox component="form" onSubmit={xacNhanNhapTay}>
                      {loiCamera && (
                        <MDBox mb={2}>
                          <MDAlert color="warning">{loiCamera}</MDAlert>
                        </MDBox>
                      )}
                      <MDInput
                        label="Nhập/quét mã QR Sọt"
                        value={maQr}
                        onChange={(e) => setMaQr(e.target.value)}
                        fullWidth
                        autoFocus
                        required
                      />
                      {loi && (
                        <MDBox mt={2}>
                          <MDAlert color="error">{loi}</MDAlert>
                        </MDBox>
                      )}
                      <MDBox mt={2.5}>
                        <MDButton
                          type="submit"
                          variant="gradient"
                          color="info"
                          fullWidth
                          disabled={dangTai}
                        >
                          {dangTai ? "Đang kiểm tra..." : "Xác nhận"}
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  )}

                  {dungCamera && loi && (
                    <MDBox mt={2}>
                      <MDAlert color="error">{loi}</MDAlert>
                    </MDBox>
                  )}
                </MDBox>
              </Card>
            )}

            {daXuLy.length > 0 && (
              <MDBox mt={3}>
                <Card>
                  <MDBox px={{ xs: 2.5, sm: 3 }} pt={2.5} pb={1}>
                    <NhanDau>Phiên làm việc</NhanDau>
                    <MDTypography variant="h5" component="h2">
                      Đã xử lý
                    </MDTypography>
                    <MDTypography variant="button" fontWeight="regular" color="text">
                      {daXuLy.length} Sọt kể từ khi mở trang này.
                    </MDTypography>
                  </MDBox>
                  <List sx={{ py: 0 }}>
                    {daXuLy.map((p, i) => (
                      <MDBox key={`${p.maSot}_${p.luc}`}>
                        <ListItem
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 1,
                            py: 1.5,
                            px: { xs: 2.5, sm: 3 },
                          }}
                        >
                          <MDBox display="flex" alignItems="center" gap={1} minWidth={0}>
                            <Chip
                              size="small"
                              label={p.loai === "nhap" ? "Nhập" : "Xuất"}
                              color={p.loai === "nhap" ? "success" : "warning"}
                            />
                            <MDTypography variant="button" fontWeight="medium">
                              {p.maSot}
                            </MDTypography>
                          </MDBox>
                          <MDTypography variant="button" color="text" sx={{ whiteSpace: "nowrap" }}>
                            {p.tuKg ?? "—"} kg → {p.denKg} kg
                          </MDTypography>
                        </ListItem>
                        {i < daXuLy.length - 1 && <Divider sx={{ my: 0 }} />}
                      </MDBox>
                    ))}
                  </List>
                </Card>
              </MDBox>
            )}
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default NhapXuatKho;
